import { Icon } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import type { Post } from "@circle-react/types/Post";
import classnames from "classnames";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import { PostShareDropdown } from "@/react/components/PostsV3/PostViewListItem/PostShareDropdown";
import { useCurrentPostHelpers } from "@/react/components/PostsV3/useCurrentPostHelpers";

export interface PostShareOptionsProps {
  appendTo?: Element;
  button?: ReactNode;
  menuButtonEl?: "button" | "div";
  onChange?: (data: Post) => void;
  shouldRenderCustomMenuButton?: boolean;
}

export const PostShareOptions = ({
  onChange,
  button,
  shouldRenderCustomMenuButton,
  menuButtonEl = "button",
  appendTo,
}: PostShareOptionsProps) => {
  const { showShareOnWebOptions, post, getSharePostLink, sharePostData } =
    useCurrentPostHelpers({ onChangeSharePost: onChange });

  const { data: space } = useCurrentSpaceContext();

  const Component: ReactNode = button ?? (
    <Icon type="20-arrow-box-up" size={20} />
  );

  if (!showShareOnWebOptions(space)) {
    return null;
  }

  return (
    <Dropdown
      size="extra-large"
      direction="bottom-end"
      menuButtonEl={menuButtonEl}
      className="w-full"
      appendTo={appendTo}
      buttonClassName={classnames(
        "w-full focus-visible:outline-secondary focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1",
        {
          "p-1 hover:bg-tertiary rounded group flex items-center":
            !shouldRenderCustomMenuButton,
        },
      )}
      button={
        shouldRenderCustomMenuButton ? (
          Component
        ) : (
          <Typography.LabelSm
            weight="medium"
            color="text-default group-hover:text-dark"
          >
            {t("post.share.title")}
          </Typography.LabelSm>
        )
      }
    >
      <PostShareDropdown
        post={post}
        getSharePostLink={getSharePostLink}
        sharePostData={sharePostData}
      />
    </Dropdown>
  );
};
